import React, { FC, useEffect, useRef } from 'react';
import { SearchInputSmall } from '../common/SearchInputSmall';
import { FilterComponent } from '../FilterComponent';
import { SelectPicker } from 'rsuite';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { IAuthState } from '../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../setup';
import { checkRoles, CLAIMS } from '../../utils/roles-helper';
import { makeSearchParams, parseSearchParams } from '../../utils/utilities';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'use-debounce';

export interface IMerchantsFilters {
	status?: string,
	pid?: string,
	integrationPartnerId?: string,
	pageNumber?: number,
	pageSize?: number,
	q?: string,
	mid?: string,
}

interface Props {
	status: string,
	pid: string,
	integrationPartnerId: string,
	q: string,
	setStatus: (status: string) => void,
	setPid: (pid: string) => void,
	setIntegrationPartnerId: (integrationPartnerId: string) => void,
	setQ: (q: string) => void,
	isMerchantListLoading: boolean,
}
export const MerchantsListFilters: FC<Props> = (props) => {

	const {
		status, pid, integrationPartnerId, q,
		setStatus, setPid, setIntegrationPartnerId, setQ, isMerchantListLoading
	} = props;

	const { t } = useTranslation();
	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
	const isMounted = useRef(false);
	const { pathname, search } = useLocation();
	const navigate = useNavigate();

	const { merchantState } = useMerchants();
	const {
		merchantsLists,
		integrationPartnersListSelector,
		whitelabelPartnersListSelector,
		chosenMerchant,
	} = merchantState;

	const filtersFromUrl = parseSearchParams(search) || {};

	// const [searchValue, setSearchValue] = useState<string>(filtersFromUrl?.q ?? q);
	const [value] = useDebounce(q.toLowerCase().trim(), 1000);

	const getStatusFilter = (): string => {
		if (status === '1') {
			return 'true';
		}
		if (status === '2') {
			return 'false';
		}
		return 'null';
	};

	useEffect(() => {
		if (!isMounted.current) return;

		const params: IMerchantsFilters = filtersFromUrl;

		params.q = q;
		params.status = getStatusFilter();
		params.pid = pid;
		params.integrationPartnerId = integrationPartnerId;
		params.mid = chosenMerchant;
		params.pageNumber = 1;

		if (!params?.pageSize) {
			params.pageSize = 10;
		}

		navigate(`${pathname}?${makeSearchParams(params)}`);
	}, [value, pid, integrationPartnerId, status, chosenMerchant]);

	useEffect(() => {
		if (!isMounted.current) {
			// first mount
			isMounted.current = true;
		}
	}, []);

	return (
		<div className="card bg-transparent">
			<div className="card-body py-0 px-0">
				<div className="d-flex align-items-center block-width application-selector mb-4">
					<SearchInputSmall
						placeholder={t('MERCHANTS_LIST.SEARCH_MERCHANT')}
						searchValue={q}
						setSearchValue={setQ}
					/>
				</div>
				<div className="d-flex align-items-center fc-filter-line">
					<FilterComponent value={status} label={t('MERCHANTS_LIST.SELECT_STATUS')} labelPlacement="integrated">
						<SelectPicker
							data={merchantsLists.merchantStatuses}
							appearance="subtle"
							placement="bottomEnd"
							placeholder={t('MERCHANTS_LIST.SELECT_STATUS')}
							onSelect={(status): void => setStatus(status)}
							className={clsx('fc-selector timezone-selector', status ? 'fc-selector-cleanable' : 'fc-added-icon')}
							onClean={(): void => setStatus('')}
							searchable={false}
							value={status}
							disabled={isMerchantListLoading}
						/>
					</FilterComponent>

					{
						checkRoles(authUser, CLAIMS.SUPER_ADMIN) && (
							<FilterComponent value={pid} label={t('MERCHANTS_LIST.WHITELABEL_PARTNERS')} labelPlacement="integrated">
								<SelectPicker
									data={whitelabelPartnersListSelector}
									appearance="subtle"
									placement="bottomEnd"
									placeholder={t('MERCHANTS_LIST.WHITELABEL_PARTNERS')}
									onSelect={(pid): void => setPid(pid)}
									className={clsx('fc-selector timezone-selector', pid ? 'fc-selector-cleanable' : 'fc-added-icon')}
									onClean={(): void => setPid('')}
									value={pid}
									disabled={isMerchantListLoading}
								/>
							</FilterComponent>
						)
					}
					{
						checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN) && (
							<FilterComponent value={integrationPartnerId} label={t('MERCHANTS_LIST.INTEGRATION_PARTNERS')} labelPlacement="integrated">
								<SelectPicker
									data={integrationPartnersListSelector}
									appearance="subtle"
									placement="bottomEnd"
									placeholder={t('MERCHANTS_LIST.INTEGRATION_PARTNERS')}
									onSelect={(pid): void => setIntegrationPartnerId(pid)}
									className={clsx('fc-selector timezone-selector', integrationPartnerId ? 'fc-selector-cleanable' : 'fc-added-icon')}
									onClean={(): void => setIntegrationPartnerId('')}
									value={integrationPartnerId}
									disabled={isMerchantListLoading}
								/>
							</FilterComponent>
						)
					}
				</div>
			</div>
		</div>
	);
};
